import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { NgIf, NgFor, NgClass } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ConfiguracaoStepper, SecaoStepper, EtapaStepper, EventoNavegacaoStepper } from './interfaces/stepper.interface';
import { StepperEstadoService } from '../../services/stepper-estado.service';
import { ValidacaoEtapaService } from '../../services/validacao-etapa/validacao-etapa.service';

@Component({
  selector: 'app-stepper-navigation',
  standalone: true,
  imports: [NgIf, NgFor, NgClass, RouterModule],
  templateUrl: './stepper-navigation.component.html',
  styleUrl: './stepper-navigation.component.scss'
})
export class StepperNavigationComponent implements OnInit, OnDestroy {
  @Input() configuracao?: ConfiguracaoStepper;

  @Output() navegacao = new EventEmitter<EventoNavegacaoStepper>();

  public configuracaoAtual?: ConfiguracaoStepper;
  public etapaAtual: number = 1;

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private stepperEstadoService: StepperEstadoService,
    private validacaoEtapaService: ValidacaoEtapaService
  ) { }

  ngOnInit(): void {
    // Se não foi passada configuração via Input, usa o serviço
    if (!this.configuracao) {
      this.stepperEstadoService.obterConfiguracaoAtual()
        .pipe(takeUntil(this.destroy$))
        .subscribe(configuracao => {
          this.configuracaoAtual = configuracao;
        });
    } else {
      this.configuracaoAtual = this.configuracao;
    }

    // Observa mudanças na etapa atual
    this.stepperEstadoService.obterEtapaAtual()
      .pipe(takeUntil(this.destroy$))
      .subscribe(etapaId => {
        this.etapaAtual = etapaId;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Verifica se uma seção inteira está desabilitada
  secaoDesabilitada(secaoId: number): boolean {
    if (!this.configuracaoAtual) return false;

    const secao = this.configuracaoAtual.secoes.find(s => s.id === secaoId);
    if (!secao) return false;

    return secao.etapas.every(etapa => etapa.desabilitado);
  }

  // Obtém a última etapa habilitada
  obterUltimaEtapa(): number {
    if (!this.configuracaoAtual) return 1;

    const etapasHabilitadas = this.obterEtapasHabilitadas();
    return etapasHabilitadas.length > 0
      ? etapasHabilitadas[etapasHabilitadas.length - 1].id
      : 1;
  }

  // Obtém todas as etapas habilitadas
  private obterEtapasHabilitadas(): EtapaStepper[] {
    if (!this.configuracaoAtual) return [];

    return this.configuracaoAtual.secoes
      .flatMap(secao => secao.etapas)
      .filter(etapa => !etapa.desabilitado);
  }

  // Navega para etapa anterior
  etapaAnterior(): void {
    const etapasHabilitadas = this.obterEtapasHabilitadas();
    const indiceAtual = etapasHabilitadas.findIndex(etapa => etapa.id === this.etapaAtual);

    if (indiceAtual > 0) {
      const etapaAnterior = etapasHabilitadas[indiceAtual - 1];
      this.navegarParaEtapa(etapaAnterior.rota, etapaAnterior.id);
    }
  }

  // Navega para próxima etapa com validação (BOTÃO)
  async proximaEtapa(): Promise<void> {
    const etapasHabilitadas = this.obterEtapasHabilitadas();
    const indiceAtual = etapasHabilitadas.findIndex(etapa => etapa.id === this.etapaAtual);

    // Se é a última etapa, gera resultado
    if (indiceAtual === etapasHabilitadas.length - 1) {
      this.gerarResultado();
      return;
    }

    if (indiceAtual < etapasHabilitadas.length - 1) {
      const proximaEtapa = etapasHabilitadas[indiceAtual + 1];

      // Valida antes de navegar
      const podeNavegar = await this.validacaoEtapaService.validarParaNavegacao();

      if (podeNavegar) {
        // Executa navegação após validar
        this.navegarSemValidacao(proximaEtapa.rota, proximaEtapa.id);
      }
    }
  }

  // Navega para uma etapa específica com validação ao clicar na etapa no stepper
  async navegarParaEtapa(rota: string, etapaId?: number, desabilitado: boolean = false): Promise<void> {
    if (desabilitado) return;
    const podeNavegar = await this.validacaoEtapaService.validarParaNavegacao();

    if (podeNavegar) {
      this.navegarSemValidacao(rota, etapaId);
    }
  }

  private navegarSemValidacao(rota: string, etapaId?: number): void {
    const evento: EventoNavegacaoStepper = {
      etapaOrigemId: this.etapaAtual,
      etapaDestinoId: etapaId || this.etapaAtual,
      rota
    };

    this.navegacao.emit(evento);

    // Atualiza etapa atual no serviço
    if (etapaId) {
      this.stepperEstadoService.definirEtapaAtual(etapaId);
    }

    // Navegação dentro do contexto de edição
    const caminhoBase = this.router.url.split('/').slice(0, 2).join('/');
    this.router.navigate([`${caminhoBase}${rota}`]);
  }

  gerarResultado(): void {
    const rota = this.configuracaoAtual?.rotaResultado || this.stepperEstadoService.getRotaResultado();
    if (rota && rota !== '/home') {
      this.router.navigate([rota]);
    }
  }

  voltarAoInicio(): void {
    const rota = this.configuracaoAtual?.rotaInicio || '/home';
    this.router.navigate([rota]);
  }

  podeVoltarEtapa(): boolean {
    const etapasHabilitadas = this.obterEtapasHabilitadas();
    const indiceAtual = etapasHabilitadas.findIndex(etapa => etapa.id === this.etapaAtual);
    return indiceAtual > 0;
  }

  // Verifica se uma etapa está ativa (atual)
  etapaAtiva(etapaId: number): boolean {
    return this.etapaAtual === etapaId;
  }

  // Verifica se uma etapa foi concluída
  etapaConcluida(etapaId: number): boolean {
    const etapasHabilitadas = this.obterEtapasHabilitadas();
    const indiceAtual = etapasHabilitadas.findIndex(
      (etapa) => etapa.id === this.etapaAtual,
    );
    const indiceEtapa = etapasHabilitadas.findIndex(
      (etapa) => etapa.id === etapaId,
    );
    return indiceAtual > indiceEtapa;
  }

  // Verifica se uma seção foi concluída (todas etapas passadas)
  secaoConcluida(secaoId: number): boolean {
    if (!this.configuracaoAtual) return false;

    const secao = this.configuracaoAtual.secoes.find(s => s.id === secaoId);
    if (!secao) return false;

    const ultimaEtapaSecao = secao.etapas
      .filter(etapa => !etapa.desabilitado)
      .reduce((ultima, etapa) => etapa.id > ultima ? etapa.id : ultima, 0);

    return this.etapaAtual > ultimaEtapaSecao;
  }

}